import * as m from '@/paraglide/messages';
import { CoverImgWithBackgroundNew } from '@/components/shared-ui/audio/cover-img-with-background';
import { Spinner } from '@/components/shared-ui/spinner';
import { useAnalytics } from '@listening/frontend/analytics/client';
import { audioDetailQueryOptions } from '@/lib/audio-detail';
import { audioService } from '@/services/audio-service';
import PremiumLockIcon from '@listening/assets/icons/premium-lock.svg?react';
import ShareIcon from '@listening/assets/icons/share2.svg?react';
import type { AudioItemDetail } from '@listening/api-contract/legacy-client-types';
import { cn, useContextAndErrorIfNull } from '@listening/utils/react-helpers';
import { dialogSurface } from '@/components/web-fe/design-v2/surfaces';
import { capitalizeFirstLetter } from '@listening/utils/ts-utils';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@listening/frontend/components/radix/accordion';
import { Button } from '@listening/frontend/components/radix/button';
import {
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@listening/frontend/components/radix/dialog';
import { Input } from '@listening/frontend/components/radix/input';
import { Label } from '@listening/frontend/components/radix/label';
import {
  RadioGroup,
  RadioGroupItem,
} from '@listening/frontend/components/radix/radio-group';
import { Separator } from '@listening/frontend/components/radix/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@listening/frontend/components/radix/tooltip';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { NavArrowLeft, NavArrowRight } from 'iconoir-react';
import React, { createContext, useState } from 'react';
import { getDisplayAudioTitle } from '@/lib/audio-display-metadata';
import { CopyTextInputAndButton } from './forward-email-dialog';
import { useCopyText } from '@/lib/clipboard';

type ScreenSteps = 'initial' | 'visibility';

const ShareDialogContext = createContext<{
  audioId: string;
  setScreen: (screen: ScreenSteps) => void;
  screen: ScreenSteps;
  audioDetailQuery: UseQueryResult<AudioItemDetail>;
  publicLink: string;
} | null>(null);

const useShareDialogContext = () =>
  useContextAndErrorIfNull(ShareDialogContext);

// Was a hardcoded dark panel (`bg-slate-900` + `text-[var(--v2-text-primary)]` in both themes).
// On the shared surface it follows the active theme like every other modal.
export const sharingDialogContentClassName = cn(
  'max-w-[400px] gap-0 overflow-hidden p-0',
  dialogSurface,
);

function ShareAudioDialogV1({ audioId }: { audioId: string }) {
  const [screen, setScreen] = useState<ScreenSteps>('initial');
  const audioDetailQuery = useQuery(audioDetailQueryOptions(audioId));
  const publicLink = `${window.location.origin}/doc/${encodeURIComponent(audioDetailQuery.data?.url_slug ?? audioId)}`;
  return (
    <DialogContent className={sharingDialogContentClassName}>
      <div className='w-full min-w-0 overflow-hidden'>
        <ShareDialogContext
          value={{ audioId, setScreen, screen, audioDetailQuery, publicLink }}
        >
          {screen === 'initial' && <InitialStep />}
          {screen === 'visibility' && <VisibilityStep />}
        </ShareDialogContext>
      </div>
    </DialogContent>
  );
}

export function ShareAudioDialog({ audioId }: { audioId: string }) {
  return <ShareAudioDialogV1 audioId={audioId} />;
}

function InitialStep() {
  const { setScreen, audioDetailQuery, publicLink } = useShareDialogContext();
  const { copy, copied: showCopied } = useCopyText();
  const { capture } = useAnalytics();
  const displayTitle = getDisplayAudioTitle(audioDetailQuery.data?.title);
  return (
    <>
      <div className='flex flex-col gap-6 border-b border-[var(--v2-card-border)] px-5 py-6'>
        <DialogHeader className='flex flex-col gap-1.5'>
          <DialogTitle className='text-left text-lg font-semibold text-[var(--v2-text-primary)]'>
            {m.dialog_share_audio()}
          </DialogTitle>
          <DialogDescription className='text-left text-sm text-[var(--v2-text-muted)]'>
            {m.dialog_share_audio_desc()}
          </DialogDescription>
        </DialogHeader>
        <div className='h-[200px] overflow-hidden rounded-xl bg-[var(--v2-card-bg)] p-4'>
          <img
            src={audioDetailQuery.data?.cover_img_url ?? ''}
            alt='Audio Cover'
            className='w-full rounded-lg border border-[var(--v2-card-border)] shadow-sm'
          />
        </div>
        <Separator className='bg-white/10' />
        <div className='flex gap-4'>
          <div className='relative size-12'>
            <CoverImgWithBackgroundNew size='sm' title={displayTitle} />
          </div>
          <div className='flex flex-col gap-1'>
            <span className='font-semibold text-[var(--v2-text-primary)]'>
              {displayTitle}
            </span>
            <span className='text-sm text-[var(--v2-text-muted)]'>
              Chapter count: {audioDetailQuery.data?.chapters?.length ?? 0}
            </span>
          </div>
        </div>
        <div className='flex flex-col gap-3'>
          <Tooltip {...(showCopied && { open: true })}>
            <TooltipTrigger asChild>
              <div className='w-full'>
                <Button
                  className='flex h-11 w-full gap-2 rounded-full bg-[var(--v2-accent)] text-white hover:bg-[var(--v2-accent)]'
                  onClick={() => {
                    if (!audioDetailQuery.data)
                      throw new Error('Audio detail not found');
                    if (
                      audioDetailQuery.data.sharing?.visibility === 'private'
                    ) {
                      setScreen('visibility');
                      return;
                    }
                    capture('share_document_ui_interaction', {
                      interaction: 'sharing_link_copied_in_modal',
                      audio_id: audioDetailQuery.data.id,
                    });
                    void copy(publicLink);
                  }}
                >
                  <ShareIcon className='text-white' />
                  <span>{m.common_share()}</span>
                </Button>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              {(() => {
                if (showCopied) return m.dialog_copied();
                if (audioDetailQuery.data?.sharing?.visibility === 'private')
                  return m.dialog_cannot_share_private();
                return m.common_share();
              })()}
            </TooltipContent>
          </Tooltip>
        </div>
      </div>

      <div className='flex items-center justify-between px-5 py-3'>
        <span className='font-semibold text-[var(--v2-text-primary)]'>
          {m.dialog_visibility()}
        </span>
        <Button
          disabled={!audioDetailQuery.data}
          variant='ghost'
          className='text-[var(--v2-text-primary)] hover:bg-black/[0.05] hover:text-[var(--v2-text-primary)] dark:hover:bg-white/10'
          onClick={() => {
            setScreen('visibility');
          }}
        >
          {audioDetailQuery.isPending && <Spinner />}
          {audioDetailQuery.data && (
            <span>
              {audioDetailQuery.data.sharing?.visibility === 'private'
                ? m.common_just_me()
                : capitalizeFirstLetter(
                    audioDetailQuery.data.sharing?.visibility ??
                      m.common_just_me(),
                  )}
            </span>
          )}

          <NavArrowRight className='ml-1 size-4 text-[var(--v2-text-primary)]' />
        </Button>
      </div>
    </>
  );
}

function VisibilityStep() {
  const { setScreen, audioId, audioDetailQuery, publicLink } =
    useShareDialogContext();
  const { capture } = useAnalytics();

  const initialVisibility =
    audioDetailQuery.data?.sharing?.visibility ?? 'private';
  const [newVisibility, setNewVisibility] = useState(initialVisibility);
  const initialSharedWith = audioDetailQuery.data?.sharing?.shared_with;
  const [newSharedWith, setNewSharedWith] = useState(
    initialSharedWith
      ? {
          emails: initialSharedWith.emails,
          user_ids: initialSharedWith.user_ids,
        }
      : undefined,
  );

  const updateAudioMutation = audioService.mutations.useUpdateAudioMutation();

  if (!audioDetailQuery.data) return <Spinner />;

  const hasPendingChanges =
    newVisibility !== initialVisibility ||
    !stringListEquals(
      newSharedWith?.emails ?? [],
      initialSharedWith?.emails ?? [],
    ) ||
    !stringListEquals(
      newSharedWith?.user_ids ?? [],
      initialSharedWith?.user_ids ?? [],
    );

  return (
    <>
      <SharingDialogVisibilityStepHeader
        onBackClicked={() => {
          setScreen('initial');
        }}
      />
      <SharingDialogVisibilityStepContainer>
        <SharingDialogVisibilityRadioButtons
          visibility={newVisibility}
          setVisibility={setNewVisibility}
        />
        {newVisibility === 'public' && (
          <SharingDialogVisibilityStepPublicLinkCopyBox
            publicLink={publicLink}
          />
        )}
        {newVisibility === 'selective_share' && (
          <div>
            <Accordion type='multiple'>
              <AccordionItem value='user_ids'>
                <AccordionTrigger className='text-[var(--v2-text-primary)]'>
                  {m.dialog_user_ids()}
                </AccordionTrigger>
                <AccordionContent>
                  <AddOrRemoveStringFromList
                    values={newSharedWith?.user_ids ?? []}
                    setNewValues={(v) => {
                      setNewSharedWith({
                        emails: newSharedWith?.emails,
                        user_ids: v,
                      });
                    }}
                  />
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value='emails'>
                <AccordionTrigger className='text-[var(--v2-text-primary)]'>
                  {m.dialog_emails()}
                </AccordionTrigger>
                <AccordionContent>
                  <AddOrRemoveStringFromList
                    values={newSharedWith?.emails ?? []}
                    setNewValues={(v) => {
                      setNewSharedWith({
                        user_ids: newSharedWith?.user_ids,
                        emails: v,
                      });
                    }}
                  />
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        )}
        <SharingDialogVisibilityStepSubmitButton
          disabled={!hasPendingChanges}
          onClick={() => {
            if (newVisibility === 'selective_share')
              throw new Error('Not implemented');
            capture('share_document_visibility_change', {
              visibility: newVisibility,
              audio_id: audioId,
            });
            updateAudioMutation.mutate({
              audioId,
              updates: {
                visibility: newVisibility,
                shared_with_emails: newSharedWith?.emails,
                shared_with_user_ids: newSharedWith?.user_ids,
              },
            });
          }}
          isPending={updateAudioMutation.isPending}
          label={m.common_save()}
        />
      </SharingDialogVisibilityStepContainer>
    </>
  );
}

function stringListEquals(a: ReadonlyArray<string>, b: ReadonlyArray<string>) {
  return a.length === b.length && a.every((v) => b.includes(v));
}

function AddOrRemoveStringFromList({
  values,
  setNewValues,
}: {
  values: ReadonlyArray<string>;
  setNewValues: (values: Array<string>) => void;
}) {
  const [newString, setNewString] = useState('');
  return (
    <div>
      {values.map((value, idx) => (
        <div key={value}>
          <Input
            value={value}
            className='border-[var(--v2-card-border)] bg-[var(--v2-card-bg)] text-[var(--v2-text-primary)]'
            onChange={(e) => {
              setNewValues(
                values.map((v, i) => (i === idx ? e.target.value : v)),
              );
            }}
          />
          <Button
            variant='ghost'
            className='text-[var(--v2-text-primary)] hover:text-[var(--v2-text-muted)]'
            onClick={() => {
              setNewValues(values.filter((_, i) => i !== idx));
            }}
          >
            {m.dialog_remove()}
          </Button>
        </div>
      ))}
      <Input
        value={newString}
        className='border-[var(--v2-card-border)] bg-[var(--v2-card-bg)] text-[var(--v2-text-primary)]'
        onChange={(e) => {
          setNewString(e.target.value);
        }}
      />
      <Button
        variant='ghost'
        className='text-[var(--v2-text-primary)] hover:text-[var(--v2-text-muted)]'
        onClick={() => {
          setNewValues([...values, newString]);
          setNewString('');
        }}
      >
        {m.common_add()}
      </Button>
    </div>
  );
}

export function SharingDialogVisibilityStepHeader({
  onBackClicked,
}: {
  onBackClicked: () => void;
}) {
  return (
    <DialogHeader className='flex flex-row items-center gap-2 border-b border-[var(--v2-card-border)] px-4 py-5'>
      <Button
        className='p-1 text-[var(--v2-text-primary)] hover:bg-black/[0.05] dark:hover:bg-white/10'
        onClick={onBackClicked}
        variant='ghost'
      >
        <NavArrowLeft />
      </Button>
      <div>
        <DialogTitle className='text-[var(--v2-text-primary)]'>
          {m.dialog_visibility()}
        </DialogTitle>
        <DialogDescription className='text-[var(--v2-text-muted)]'>
          {m.dialog_choose_visibility()}
        </DialogDescription>
      </div>
    </DialogHeader>
  );
}

export type SharingVisibility = Exclude<
  NonNullable<AudioItemDetail['sharing']>['visibility'],
  undefined
>;

export function SharingDialogVisibilityRadioButtons({
  visibility,
  setVisibility,
}: {
  visibility: SharingVisibility;
  setVisibility: (visibility: SharingVisibility) => void;
}) {
  const options: Record<
    SharingVisibility,
    { label: string; description: string; restricted?: boolean }
  > = {
    public: {
      label: m.dialog_visibility_public(),
      description: m.dialog_visibility_public_desc(),
    },
    selective_share: {
      label: m.dialog_visibility_unlisted(),
      description: m.dialog_visibility_unlisted_desc(),
    },
    private: {
      label: m.common_just_me(),
      description: m.dialog_visibility_just_me_desc(),
      restricted: true,
    },
  };
  return (
    <RadioGroup
      value={visibility}
      onValueChange={(v) => {
        setVisibility(v as SharingVisibility);
      }}
      className='flex flex-col'
    >
      {Object.entries(options)
        .filter(([key]) => key !== 'selective_share')
        .map(([key, option], idx) => (
          <React.Fragment key={key}>
            {idx !== 0 && <Separator className='bg-white/10' />}
            <Label
              className={cn(
                'flex cursor-pointer gap-3 rounded-lg px-0 py-4 hover:bg-white/5',
                option.restricted && 'opacity-50',
              )}
              key={key}
              htmlFor={key}
            >
              <RadioGroupItem
                value={key}
                id={key}
                className='mt-1 border-[var(--v2-card-border)] text-[var(--v2-text-primary)]'
              />
              <div className='flex flex-col gap-1'>
                <span className='flex items-center gap-2 leading-5 font-medium text-[var(--v2-text-primary)]'>
                  {option.label}
                  {option.restricted && <PremiumLockIcon className='size-4' />}
                </span>
                <span className='leading-6 font-normal text-[var(--v2-text-muted)]'>
                  {option.description}
                </span>
              </div>
            </Label>
          </React.Fragment>
        ))}
    </RadioGroup>
  );
}

function SharingDialogVisibilityStepPublicLinkCopyBox({
  publicLink,
}: {
  publicLink: string;
}) {
  return (
    <div>
      <CopyTextInputAndButton text={publicLink} />
    </div>
  );
}

export function SharingDialogVisibilityStepContainer({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <div className='flex flex-col gap-6 bg-[var(--v2-card-bg)] p-6 text-[var(--v2-text-primary)]'>
      {children}
    </div>
  );
}

export function SharingDialogVisibilityStepSubmitButton({
  disabled,
  onClick,
  isPending,
  label,
}: {
  disabled: boolean;
  onClick: () => void;
  isPending: boolean;
  label: string;
}) {
  return (
    <Button
      disabled={disabled || isPending}
      className='flex h-11 gap-4 rounded-full bg-[var(--v2-accent)] text-[var(--v2-text-primary)] hover:bg-[var(--v2-accent)]'
      onClick={onClick}
    >
      {isPending && <Spinner />}
      {label}
    </Button>
  );
}
